import { css, keyframes } from '@emotion/react';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Box, Button, Center, ClappingHandsOutline, HStack, IconButton, Image, Spinner, Text, useToast, VStack, XOutline } from '@bonusly/design-system';
import { ChevronLeftOutline, ChevronRightOutline } from '@bonusly/ds-icon';
import { useDispatch, useEventListener } from '@bonusly/events';
import { useCurrentUser } from '@bonusly/who-am-i';

import spotlightBackground1 from '../../../assets/images/spotlight-background-1.svg';
import spotlightBackground2 from '../../../assets/images/spotlight-background-2.svg';
import { useDeleteSpotlightSection, useSpotlight, useUpdateSpotlightSection } from '../../../queries/employee-spotlights';
import { useUser } from '../../../queries/user';
import { AttachmentCategory, EmployeeSpotlightAttachment } from '../../action-input/lib/entities/attachment';
import { GiveBoxEvent } from '../../recognition/give-box/events';
import { DeleteUserSpotlightSectionModal } from './delete-user-spotlight-section-modal';
import { UserSpotlightCallToActionCard } from './user-spotlight-call-to-action-card';
import { UserSpotlightCard } from './user-spotlight-card';
import { deriveUserSpotlightCtaPersona, isUserSpotlightCtaPersona, userSpotlightCtaPersonas } from './user-spotlight-cta-persona';
import { formatUserSpotlightDateRange } from './user-spotlight-date-range';
import { UserSpotlightDebugBubbles } from './user-spotlight-debug-bubbles';
import { UserSpotlightIntroCard } from './user-spotlight-intro-card';
import { UserSpotlightNavigation } from './user-spotlight-navigation';

// Ripple animation - continuous expansion that fades in and out for seamless looping
const ripple = keyframes`
	0% {
		transform: scale(1) translateY(0);
		opacity: 0;
	}
	25% {
		transform: scale(1.2) translateY(-2%);
		opacity: 0.5;
	}
	50% {
		transform: scale(1.4) translateY(-4%);
		opacity: 1;
	}
	75% {
		transform: scale(1.6) translateY(-6%);
		opacity: 0.75;
	}
	100% {
		transform: scale(1.8) translateY(-8%);
		opacity: 0;
	}
`;

// Background theme variations for debug mode
const backgroundThemes = [
	{
		nameKey: 'default',
		position: 'top center',
		transform: 'none',
		filter: 'none',
		overlayPosition: 'top center',
		overlayTransform: 'none',
	},
	{
		nameKey: 'bottomRight',
		position: 'bottom right',
		transform: 'none',
		filter: 'none',
		overlayPosition: 'bottom right',
		overlayTransform: 'none',
	},
	{
		nameKey: 'flipped',
		position: 'top center',
		transform: 'scaleX(-1)',
		filter: 'none',
		overlayPosition: 'top center',
		overlayTransform: 'scaleX(-1)',
	},
	{
		nameKey: 'warmTint',
		position: 'top center',
		transform: 'none',
		filter: 'hue-rotate(30deg) saturate(1.2)',
		overlayPosition: 'top center',
		overlayTransform: 'none',
	},
	{
		nameKey: 'coolTint',
		position: 'top center',
		transform: 'none',
		filter: 'hue-rotate(-30deg) saturate(1.1)',
		overlayPosition: 'top center',
		overlayTransform: 'none',
	},
	{
		nameKey: 'sunset',
		position: 'bottom left',
		transform: 'rotate(180deg)',
		filter: 'hue-rotate(60deg) saturate(1.3)',
		overlayPosition: 'bottom left',
		overlayTransform: 'rotate(180deg)',
	},
	{
		nameKey: 'ocean',
		position: 'center',
		transform: 'scaleY(-1)',
		filter: 'hue-rotate(-60deg) saturate(1.2)',
		overlayPosition: 'center',
		overlayTransform: 'scaleY(-1)',
	},
];

interface BackgroundGradientProps {
	themeIndex?: number;
}

const BackgroundGradient = ({ themeIndex = 0 }: BackgroundGradientProps) => {
	const theme = backgroundThemes[themeIndex % backgroundThemes.length];

	return (
		<Box position="fixed" top={0} left={0} width="100vw" height="100vh" zIndex={0} overflow="hidden" pointerEvents="none" bg="bg.surface">
			<Box position="absolute" top={0} left={0} right={0} bottom={0} transform={theme.transform} filter={theme.filter}>
				<Image
					src={spotlightBackground1}
					alt=""
					aria-hidden="true"
					position="absolute"
					top={0}
					left={0}
					w="100%"
					h="100%"
					objectFit="cover"
					objectPosition={theme.position}
					pointerEvents="none"
					userSelect="none"
					draggable={false}
				/>
			</Box>

			{/* Blur layer */}
			<Box
				position="fixed"
				top={0}
				left={0}
				width="100%"
				height="100%"
				backdropFilter="blur(560px)"
				css={css`
					-webkit-backdrop-filter: blur(560px);
				`}
				zIndex={0}
				pointerEvents="none"
			/>
			{/* Animated overlay */}
			<Box position="fixed" top={0} left={0} width="100%" height="100%" transform={theme.overlayTransform} zIndex={1} pointerEvents="none">
				<Image
					src={spotlightBackground2}
					alt=""
					aria-hidden="true"
					position="absolute"
					top={0}
					left={0}
					w="100%"
					h="100%"
					objectFit="cover"
					objectPosition={theme.overlayPosition}
					pointerEvents="none"
					userSelect="none"
					draggable={false}
					animation={`${ripple} 16s linear infinite`}
				/>
			</Box>
		</Box>
	);
};
interface UserSpotlightViewerProps {
	spotlightId: string;
}

export const UserSpotlightViewer: FC<UserSpotlightViewerProps> = ({ spotlightId }) => {
	const { t, i18n } = useTranslation('spotlight');
	const { data: spotlight, isLoading: isLoadingSpotlight, error } = useSpotlight(spotlightId);
	const currentUser = useCurrentUser();
	// Prefer embedded user data from spotlight (avoids extra API call, works for spoofed users)
	// Fall back to useUser API call if not present (backwards compatibility)
	const embeddedUser = spotlight?.user;
	const { data: fetchedUser, isLoading: isLoadingUser } = useUser(embeddedUser ? undefined : spotlight?.userId);
	const user = embeddedUser ?? fetchedUser;
	const isLoading = isLoadingSpotlight || (!embeddedUser && isLoadingUser);
	const [currentIndex, setCurrentIndex] = useState(0);
	const [sectionToDelete, setSectionToDelete] = useState<{ id: string; title: string } | null>(null);
	const [isEditingSection, setIsEditingSection] = useState(false);
	const [backgroundThemeIndex, setBackgroundThemeIndex] = useState(0);
	const [searchParams, setSearchParams] = useSearchParams();
	const toast = useToast();
	const isDebugMode = searchParams.get('debug') === 'true';
	const ctaPersonaParam = searchParams.get('ctaPersona');
	const debugCtaPersona = isDebugMode && isUserSpotlightCtaPersona(ctaPersonaParam) ? ctaPersonaParam : null;
	const hideCta = searchParams.get('hideCta') === 'true';
	// Navigation style: 'side' (default) = edge arrows, 'bottom' = traditional bottom bar
	const useBottomNav = searchParams.get('nav') === 'bottom';

	const handleCycleBackgroundTheme = useCallback(() => {
		setBackgroundThemeIndex((prev) => (prev + 1) % backgroundThemes.length);
	}, []);

	// Sync URL with current section (for shareability)
	const updateUrlSection = useCallback(
		(index: number, sections: { id: string }[]) => {
			const totalCards = sections.length + 2;
			const isIntro = index === 0;
			const isFinal = index === totalCards - 1;

			setSearchParams(
				(prev) => {
					const newParams = new URLSearchParams(prev);
					if (isIntro || isFinal) {
						newParams.delete('section');
					} else {
						const sectionIndex = index - 1; // Offset for intro card
						const section = sections[sectionIndex];
						if (section) {
							newParams.set('section', section.id);
						}
					}
					return newParams;
				},
				{ replace: true }
			);
		},
		[setSearchParams]
	);

	// Initialize from URL on load (only once when spotlight loads)
	const hasInitializedFromUrl = useRef(false);

	// Track when give box is opened from the final card for redirect on success
	const giveBoxOpenedFromFinalCard = useRef(false);
	const navigate = useNavigate();

	// Compute isFinalCard at component level so handlers can access it
	// When hideCta is true, there is no final CTA card
	const isFinalCard = spotlight && !hideCta ? currentIndex === spotlight.sections.length + 1 : false;

	// Listen for successful recognition creation and redirect to homepage if opened from final card
	useEventListener(
		GiveBoxEvent.Created,
		useCallback(() => {
			if (giveBoxOpenedFromFinalCard.current) {
				toast({
					title: t('viewer.toasts.recognitionSuccess'),
					status: 'success',
					duration: 5000,
				});
				navigate('/');
			}
			giveBoxOpenedFromFinalCard.current = false;
		}, [navigate, toast, t])
	);

	useEffect(() => {
		if (!spotlight?.sections.length || hasInitializedFromUrl.current) return;

		const sectionId = searchParams.get('section');
		if (sectionId) {
			const sectionIndex = spotlight.sections.findIndex((s) => s.id === sectionId);
			if (sectionIndex !== -1) {
				setCurrentIndex(sectionIndex + 1); // +1 for intro card offset
			}
		}
		hasInitializedFromUrl.current = true;
	}, [spotlight?.sections, searchParams]);
	const actualIsViewingOwnSpotlight = spotlight?.userId === currentUser?.id;
	const isViewingOwnSpotlight = actualIsViewingOwnSpotlight;

	// Check if current user generated this spotlight (can edit)
	const canEditSpotlight = spotlight?.generatedByUserId === currentUser?.id;
	const ctaPersona = deriveUserSpotlightCtaPersona({
		isViewingOwnSpotlight,
		canEditSpotlight: Boolean(canEditSpotlight),
		overridePersona: debugCtaPersona || undefined,
	});

	const handleCycleCtaPersona = useCallback(() => {
		setSearchParams(
			(prev) => {
				const newParams = new URLSearchParams(prev);
				const currentParam = newParams.get('ctaPersona');
				const current = isUserSpotlightCtaPersona(currentParam) ? currentParam : null;
				const currentIndex = current ? userSpotlightCtaPersonas.indexOf(current) : -1;
				const next = userSpotlightCtaPersonas[(currentIndex + 1) % userSpotlightCtaPersonas.length];
				newParams.set('ctaPersona', next);
				return newParams;
			},
			{ replace: true }
		);
	}, [setSearchParams]);

	const updateSectionMutation = useUpdateSpotlightSection({
		onSuccess: () => {
			toast({
				title: t('viewer.toasts.saveSuccess'),
				status: 'success',
				duration: 3000,
			});
		},
		onError: () => {
			toast({
				title: t('viewer.toasts.saveErrorTitle'),
				description: t('viewer.toasts.saveErrorBody'),
				status: 'error',
				duration: 5000,
			});
		},
	});

	const deleteSectionMutation = useDeleteSpotlightSection({
		onSuccess: () => {
			toast({
				title: t('viewer.toasts.removeSuccess'),
				status: 'success',
				duration: 3000,
			});
			setSectionToDelete(null);
			// Go to previous card after deletion and clear section from URL
			if (currentIndex > 0) {
				setCurrentIndex(currentIndex - 1);
			}
			setSearchParams(
				(prev) => {
					const newParams = new URLSearchParams(prev);
					newParams.delete('section');
					return newParams;
				},
				{ replace: true }
			);
		},
		onError: () => {
			toast({
				title: t('viewer.toasts.removeErrorTitle'),
				description: t('viewer.toasts.removeErrorBody'),
				status: 'error',
				duration: 5000,
			});
		},
	});

	const handleSaveSection = useCallback(
		(sectionId: string) => (textHtml: string) => {
			updateSectionMutation.mutate({
				spotlightId,
				sectionId,
				textHtml,
			});
		},
		[spotlightId, updateSectionMutation]
	);

	const handleDeleteSection = useCallback(
		(sectionId: string, sectionTitle: string) => () => {
			setSectionToDelete({ id: sectionId, title: sectionTitle });
		},
		[]
	);

	const handleConfirmDelete = useCallback(() => {
		if (sectionToDelete !== null) {
			deleteSectionMutation.mutate({
				spotlightId,
				sectionId: sectionToDelete.id,
			});
		}
	}, [spotlightId, sectionToDelete, deleteSectionMutation]);

	const handleCloseDeleteModal = useCallback(() => {
		setSectionToDelete(null);
	}, []);

	const handleNext = useCallback(() => {
		if (!spotlight) return;
		// When hideCta is true, max index is the last section (no CTA card)
		const maxIndex = hideCta ? spotlight.sections.length : spotlight.sections.length + 1;
		if (currentIndex < maxIndex) {
			const newIndex = currentIndex + 1;
			setCurrentIndex(newIndex);
			updateUrlSection(newIndex, spotlight.sections);
		}
	}, [currentIndex, spotlight, updateUrlSection, hideCta]);

	const handlePrevious = useCallback(() => {
		if (!spotlight) return;
		if (currentIndex > 0) {
			const newIndex = currentIndex - 1;
			setCurrentIndex(newIndex);
			updateUrlSection(newIndex, spotlight.sections);
		}
	}, [currentIndex, spotlight, updateUrlSection]);

	const handleGoToCard = useCallback(
		(index: number) => {
			if (!spotlight) return;
			setCurrentIndex(index);
			updateUrlSection(index, spotlight.sections);
		},
		[spotlight, updateUrlSection]
	);

	const dispatch = useDispatch();

	const handleAddToRecognition = () => {
		if (!user || !spotlight) return;

		// Track if opened from final card for redirect on success
		// Always set to current value to clear stale state from previous opens
		giveBoxOpenedFromFinalCard.current = isFinalCard;

		// Create the spotlight attachment
		const spotlightTitle = t('viewer.header.spotlightTitle', {
			name: user.displayFirstName || user.displayName || t('viewer.header.defaultName'),
			dateRange: formatUserSpotlightDateRange(spotlight.startTime, spotlight.endTime, i18n.language),
		});

		const attachment = new EmployeeSpotlightAttachment({
			category: AttachmentCategory.EmployeeSpotlight,
			id: spotlight.id,
			url: spotlight.url,
			image_url: spotlight.imageUrl || '',
			title: spotlightTitle,
			alt_text: spotlightTitle,
			width: spotlight.imageWidth ?? undefined,
			height: spotlight.imageHeight ?? undefined,
		});

		const recipient = {
			id: user.id,
			displayName: user.displayName,
			imageSrc: ('profilePic' in user ? user.profilePic : user.profilePicUrl) ?? '',
			username: user.username ?? '',
		};

		dispatch(GiveBoxEvent.Open, {
			recipient,
			attachment,
		});
	};

	const spotlightData = spotlight?.data as Record<string, unknown> | undefined;
	const receivedAnalysis = spotlightData?.receivedAnalysis as Record<string, unknown> | undefined;
	const topGiver = receivedAnalysis?.topGiver as Record<string, unknown> | undefined;
	const topGiverId = typeof topGiver?.id === 'string' ? topGiver.id : typeof topGiver?.id === 'number' ? String(topGiver.id) : undefined;
	const { data: topGiverUser } = useUser(topGiverId);

	const handleThankTopGiver = () => {
		// Track if opened from final card for redirect on success
		// Always set to current value to clear stale state from previous opens
		giveBoxOpenedFromFinalCard.current = isFinalCard;
		dispatch(GiveBoxEvent.Open);
	};

	const recognizeRecipientUser = topGiverUser || user;
	const recognizeRecipientDisplayName = topGiverUser ? topGiverUser.displayFirstName || topGiverUser.displayName : user?.displayFirstName || user?.displayName;

	const handleRecognizeTopGiver = () => {
		if (!recognizeRecipientUser) return;
		// Track if opened from final card for redirect on success
		// Always set to current value to clear stale state from previous opens
		giveBoxOpenedFromFinalCard.current = isFinalCard;
		// Build recipient with explicit username to ensure give box can render @mention correctly
		// Handle both SpotlightUser (profilePicUrl) and UserDetailEntity (profilePic) types
		const recipient = {
			id: recognizeRecipientUser.id,
			displayName: recognizeRecipientUser.displayName,
			imageSrc: ('profilePic' in recognizeRecipientUser ? recognizeRecipientUser.profilePic : recognizeRecipientUser.profilePicUrl) ?? '',
			username: recognizeRecipientUser.username ?? '',
		};
		dispatch(GiveBoxEvent.Open, {
			recipient,
		});
	};

	// Keyboard navigation - arrow keys to cycle through pages (disabled when editing or in input)
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			// Don't navigate when editing a section
			if (isEditingSection) return;

			// Don't navigate when focus is in an input, textarea, or contenteditable (e.g., give box)
			const activeElement = document.activeElement;
			if (
				activeElement instanceof HTMLInputElement ||
				activeElement instanceof HTMLTextAreaElement ||
				activeElement?.getAttribute('contenteditable') === 'true'
			) {
				return;
			}

			if (event.key === 'ArrowRight') {
				handleNext();
			} else if (event.key === 'ArrowLeft') {
				handlePrevious();
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [currentIndex, spotlight?.sections.length, isEditingSection, handleNext, handlePrevious]);

	// Touch/swipe navigation for mobile
	const touchStartX = useRef<number | null>(null);
	const touchStartY = useRef<number | null>(null);

	useEffect(() => {
		const handleTouchStart = (event: TouchEvent) => {
			if (isEditingSection) return;
			touchStartX.current = event.touches[0].clientX;
			touchStartY.current = event.touches[0].clientY;
		};

		const handleTouchEnd = (event: TouchEvent) => {
			if (isEditingSection || touchStartX.current === null || touchStartY.current === null) return;

			const touchEndX = event.changedTouches[0].clientX;
			const touchEndY = event.changedTouches[0].clientY;
			const deltaX = touchEndX - touchStartX.current;
			const deltaY = touchEndY - touchStartY.current;

			// Only trigger swipe if horizontal movement is greater than vertical (not scrolling)
			// and the swipe distance is significant enough (50px minimum)
			const minSwipeDistance = 50;
			if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > minSwipeDistance) {
				if (deltaX < 0) {
					// Swipe left = go next
					handleNext();
				} else {
					// Swipe right = go previous
					handlePrevious();
				}
			}

			touchStartX.current = null;
			touchStartY.current = null;
		};

		window.addEventListener('touchstart', handleTouchStart, { passive: true });
		window.addEventListener('touchend', handleTouchEnd, { passive: true });

		return () => {
			window.removeEventListener('touchstart', handleTouchStart);
			window.removeEventListener('touchend', handleTouchEnd);
		};
	}, [isEditingSection, handleNext, handlePrevious]);

	const renderContent = () => {
		if (isLoading || !spotlight) {
			return (
				<Center minH="100vh" position="relative" zIndex={1}>
					<VStack spacing={4}>
						<Spinner size="xl" color="success.icon" />
						<Text fontSize="lg" color="text.default">
							{t('viewer.loading')}
						</Text>
					</VStack>
				</Center>
			);
		}

		if (error || spotlight.status === 'failed') {
			return (
				<Center minH="100vh" position="relative" zIndex={1}>
					<VStack spacing={4}>
						<Text fontSize="xl" fontWeight="semibold" color="text.default">
							{t('viewer.errorTitle')}
						</Text>
						<Text fontSize="md" color="text.secondary">
							{error instanceof Error ? error.message : t('viewer.errorFallback')}
						</Text>
					</VStack>
				</Center>
			);
		}

		if (spotlight.status === 'pending' || spotlight.status === 'processing') {
			return (
				<Center minH="100vh" position="relative" zIndex={1}>
					<VStack spacing={4}>
						<Spinner size="xl" color="success.icon" />
						<Text fontSize="lg" color="text.default">
							{t('viewer.generating')}
						</Text>
					</VStack>
				</Center>
			);
		}

		// When hideCta is true, don't include the final CTA card
		const totalCards = spotlight.sections.length + (hideCta ? 1 : 2); // +1 for intro card, +1 for final card (unless hidden)
		const isIntroCard = currentIndex === 0;
		const isFinalCard = !hideCta && currentIndex === spotlight.sections.length + 1;
		const sectionIndex = currentIndex - 1; // Offset by 1 for intro card

		return (
			<>
				{/* Progress bar */}
				<Box
					position="fixed"
					top={0}
					left={0}
					h="3px"
					bg="linear-gradient(90deg, #1DB87C, #7C5CFC, #FF6B9D)"
					width={`${((currentIndex + 1) / totalCards) * 100}%`}
					transition="width 0.4s ease"
					zIndex={100}
				/>

				{/* Mobile: Simplified header with just date */}
				{!isIntroCard && (
					<Box position="fixed" top={0} left={0} right={0} zIndex={99} px={4} py={3} textAlign="right" display={{ base: 'block', md: 'none' }}>
						<Text fontSize="sm" color="text.soft" fontWeight="medium">
							{formatUserSpotlightDateRange(spotlight.startTime, spotlight.endTime, i18n.language)}
						</Text>
					</Box>
				)}

				{/* Desktop: Full header with Add to recognition button */}
				{!isIntroCard && (
					<HStack position="fixed" top={0} left={0} right={0} zIndex={99} px={4} py={3} justify="flex-end" spacing={4} display={{ base: 'none', md: 'flex' }}>
						<Text fontSize="sm" color="text.soft" fontWeight="medium">
							{t('viewer.header.spotlightTitle', {
								name: user?.displayFirstName || user?.displayName || t('viewer.header.defaultName'),
								dateRange: formatUserSpotlightDateRange(spotlight.startTime, spotlight.endTime, i18n.language),
							})}
						</Text>
						{canEditSpotlight && !hideCta && !isViewingOwnSpotlight && (
							<Button
								variant="alpha"
								colorScheme="secondary"
								size="md"
								leftIcon={<ClappingHandsOutline size="md" />}
								onClick={handleAddToRecognition}
								bg="whiteAlpha.700"
								color="gray.800"
								backdropFilter="blur(9px)"
								_hover={{ bg: 'whiteAlpha.800' }}
							>
								{t('viewer.header.addToRecognition')}
							</Button>
						)}
					</HStack>
				)}

				{/* Main content */}
				<Box
					maxW="900px"
					mx="auto"
					px={{ base: 6, md: 12 }}
					pb={isIntroCard ? { base: 8, md: 12 } : { base: useBottomNav ? 32 : 12, md: 12 }}
					minH="100vh"
					display="flex"
					flexDirection="column"
					justifyContent={{ base: isIntroCard ? 'center' : 'flex-start', md: 'center' }}
					pt={{ base: isIntroCard ? 0 : 20, md: 0 }}
					position="relative"
					zIndex={1}
					overflowY={{ base: 'auto', md: 'visible' }}
					sx={{
						'@media (max-height: 840px)': {
							pt: isIntroCard ? 0 : '56px',
							justifyContent: isIntroCard ? 'center' : 'flex-start',
							// Add padding for fixed bottom navigation on short desktop screens
							pb: isIntroCard ? undefined : '128px',
						},
					}}
				>
					<Box
						position="relative"
						width="100%"
						minH={{ base: 'auto', md: '750px' }}
						overflow="visible"
						display={isIntroCard ? 'flex' : 'block'}
						alignItems={isIntroCard ? 'center' : undefined}
						justifyContent={isIntroCard ? 'center' : undefined}
					>
						{/* Intro card */}
						<UserSpotlightIntroCard spotlight={spotlight} onExplore={handleNext} isActive={isIntroCard} fallbackUserName={user?.displayName} />

						{/* Regular cards */}
						{spotlight.sections.map((section, index) => (
							<Box key={section.id} position="relative">
								<UserSpotlightCard
									section={section}
									isActive={sectionIndex === index}
									canEdit={canEditSpotlight}
									onSave={handleSaveSection(section.id)}
									onDelete={handleDeleteSection(section.id, section.title)}
									onEditingChange={setIsEditingSection}
									isSaving={updateSectionMutation.isLoading}
									isDeleting={deleteSectionMutation.isLoading}
								/>
								{isDebugMode && <UserSpotlightDebugBubbles section={section} sectionIndex={index} isActive={sectionIndex === index} />}
							</Box>
						))}

						{/* Final card */}
						{isFinalCard && (
							<Box position="absolute" top={0} left={0} right={0} opacity={1} visibility="visible" pointerEvents="auto">
								<VStack spacing={6} align="stretch">
									<Box textAlign="center">
										<Box
											as="span"
											display="inline-block"
											px={4}
											py={2}
											borderRadius="full"
											fontSize="xs"
											fontWeight="semibold"
											textTransform="uppercase"
											letterSpacing="wide"
											mb={4}
											bg="success.bg"
											color="success.text"
										>
											{t('viewer.final.badge')}
										</Box>
										<Text variant="strong" fontSize="2xl" lineHeight="shorter" color="text.default" letterSpacing="wide">
											{ctaPersona === 'recipient' ? t('viewer.final.wrapOwn') : t('viewer.final.wrapOther')}
										</Text>
									</Box>

									<Box>
										<UserSpotlightCallToActionCard
											persona={ctaPersona}
											userDisplayFirstName={user?.displayFirstName ?? undefined}
											bonusIds={spotlight?.bonusIds ?? []}
											onAddToRecognition={handleAddToRecognition}
											onThankTopGiver={handleThankTopGiver}
											onRecognizeTopGiver={recognizeRecipientUser ? handleRecognizeTopGiver : undefined}
											recognizeTargetDisplayName={recognizeRecipientDisplayName}
											spotlightId={spotlight.id}
											subjectUserId={spotlight.userId}
											subjectDisplayName={user?.displayName ?? undefined}
										/>
									</Box>
								</VStack>
							</Box>
						)}
					</Box>

					{/* Bottom navigation - always on desktop, on mobile only when nav=bottom */}
					{!isIntroCard && (
						<Box display={{ base: useBottomNav ? 'block' : 'none', md: 'block' }}>
							<UserSpotlightNavigation
								currentIndex={currentIndex}
								totalCards={totalCards}
								onPrevious={handlePrevious}
								onNext={handleNext}
								onGoToCard={handleGoToCard}
							/>
						</Box>
					)}
				</Box>
			</>
		);
	};

	return (
		<Box minH="100vh" bg="bg.surface" position="relative">
			<BackgroundGradient themeIndex={backgroundThemeIndex} />
			{renderContent()}

			{/* Close button - only show on iOS (useful for app webviews) and not in debug mode */}
			{!isDebugMode && (
				<IconButton
					aria-label={t('viewer.iosCloseAria')}
					icon={<XOutline size="md" />}
					variant="ghost"
					colorScheme="secondary"
					position="fixed"
					top={4}
					left={4}
					zIndex={101}
					onClick={() => {
						// Prefer returning to app root (useful for iOS/Android webviews)
						window.location.assign('/');
					}}
				/>
			)}

			{/* Mobile side navigation buttons - subtle edge arrows (default, hidden when nav=bottom) */}
			{spotlight && !isLoading && !useBottomNav && currentIndex > 0 && (
				<Box
					as="button"
					aria-label={t('navigation.back')}
					position="fixed"
					left={0}
					top="50%"
					transform="translateY(-50%)"
					display={{ base: 'flex', md: 'none' }}
					alignItems="center"
					justifyContent="center"
					w="36px"
					h="80px"
					bg="whiteAlpha.600"
					backdropFilter="blur(8px)"
					borderRightRadius="full"
					color="text.soft"
					zIndex={90}
					onClick={handlePrevious}
					_hover={{ bg: 'whiteAlpha.800' }}
					_active={{ bg: 'whiteAlpha.900' }}
					sx={{ WebkitBackdropFilter: 'blur(8px)' }}
				>
					<ChevronLeftOutline size="md" />
				</Box>
			)}
			{spotlight && !isLoading && !useBottomNav && !isFinalCard && currentIndex < (spotlight?.sections.length ?? 0) + 1 && (
				<Box
					as="button"
					aria-label={t('navigation.next')}
					position="fixed"
					right={0}
					top="50%"
					transform="translateY(-50%)"
					display={{ base: 'flex', md: 'none' }}
					alignItems="center"
					justifyContent="center"
					w="36px"
					h="80px"
					bg="whiteAlpha.600"
					backdropFilter="blur(8px)"
					borderLeftRadius="full"
					color="text.soft"
					zIndex={90}
					onClick={handleNext}
					_hover={{ bg: 'whiteAlpha.800' }}
					_active={{ bg: 'whiteAlpha.900' }}
					sx={{ WebkitBackdropFilter: 'blur(8px)' }}
				>
					<ChevronRightOutline size="md" />
				</Box>
			)}

			{/* Debug controls */}
			{isDebugMode && (
				<HStack position="fixed" top={4} left={4} zIndex={101} spacing={2}>
					<Button variant="alpha" colorScheme="secondary" onClick={handleCycleBackgroundTheme}>
						Theme
					</Button>
					<Button variant="alpha" colorScheme="secondary" onClick={handleCycleCtaPersona}>
						Viewing as: {ctaPersona}
					</Button>
				</HStack>
			)}

			{/* Delete confirmation modal */}
			<DeleteUserSpotlightSectionModal
				isOpen={sectionToDelete !== null}
				onClose={handleCloseDeleteModal}
				onDelete={handleConfirmDelete}
				isDeleting={deleteSectionMutation.isLoading}
				sectionTitle={sectionToDelete?.title}
			/>
		</Box>
	);
};
